/**
 * SHARED
 * Cross-cutting code usable by any module. Must not import from any module.
 */

// TODO: assign to correct module
import { useEffect, useState } from "react";
import {
  listMyTickets,
  createTicket,
  TICKET_CATEGORIES,
  getTicketAreaGroups,
} from "../utils/ticketService.js";
import TicketThread from "./TicketThread.jsx";
import { FilePicker } from "./Attachments.jsx";

const SECTIONS = [
  { id: "docs", label: "User Documentation" },
  { id: "tickets", label: "My help tickets" },
  { id: "submit", label: "Submit a ticket" },
];

function initialTab() {
  const t = new URLSearchParams(window.location.search).get("tab");
  return SECTIONS.some((s) => s.id === t) ? t : "docs";
}

export default function Help() {
  const [tab, setTab] = useState(initialTab);
  return (
    <div className="settings-layout">
      <aside className="settings-nav">
        <h1>Help</h1>
        <nav>
          {SECTIONS.map((s) => (
            <button
              key={s.id}
              className={`settings-navitem ${tab === s.id ? "active" : ""}`}
              onClick={() => setTab(s.id)}
            >
              {s.label}
            </button>
          ))}
        </nav>
      </aside>
      <div className="settings-content">
        {tab === "docs" && <DocsSection />}
        {tab === "tickets" && <TicketsSection onSubmitClick={() => setTab("submit")} />}
        {tab === "submit" && <SubmitSection onDone={() => setTab("tickets")} />}
      </div>
    </div>
  );
}

function DocsSection() {
  return (
    <section className="settings-card">
      <h2>User Documentation</h2>
      <p className="muted">
        Documentation is on the way. In the meantime, if you're stuck, submit a ticket and
        we'll help you out.
      </p>
    </section>
  );
}

function TicketsSection({ onSubmitClick }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  async function load() {
    setErr(null);
    try {
      setTickets(await listMyTickets());
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  if (selectedId) {
    return (
      <section className="settings-card wide">
        <TicketThread
          ticketId={selectedId}
          onBack={() => { setSelectedId(null); setLoading(true); load(); }}
          onChanged={load}
        />
      </section>
    );
  }

  return (
    <section className="settings-card wide">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2 style={{ margin: 0 }}>My help tickets</h2>
        <button className="btn" onClick={onSubmitClick}>New ticket</button>
      </div>
      {loading && <p className="muted" style={{ marginTop: 16 }}>Loading…</p>}
      {err && <p className="error" style={{ marginTop: 16 }}>{err}</p>}
      {!loading && !err && tickets.length === 0 && (
        <p className="muted" style={{ marginTop: 16 }}>No tickets yet.</p>
      )}
      {tickets.length > 0 && (
        <ul className="ticket-list">
          {tickets.map((t) => (
            <li key={t.id} className="ticket-row" style={{ cursor: "pointer" }} onClick={() => setSelectedId(t.id)}>
              <div>
                <div className="ticket-subject">{t.subject}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {t.category ? `${t.category} · ` : ""}{new Date(t.last_reply_at || t.created_at).toLocaleString()}
                  {t.last_reply_role === "admin" && <strong style={{ color: "var(--primary)" }}> · new reply</strong>}
                </div>
              </div>
              <span className={`ticket-status ${t.status}`}>{t.status}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function SubmitSection({ onDone }) {
  const [subject, setSubject] = useState("");
  const [category, setCategory] = useState(TICKET_CATEGORIES[0]);
  const [module, setModule] = useState("");
  const areaGroups = getTicketAreaGroups();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setStatus(null);
    try {
      await createTicket({ subject, body, category, module, files });
      setSubject(""); setBody(""); setFiles([]); setModule("");
      setStatus({ ok: true, msg: "Ticket submitted. Our team will reply right here." });
      setTimeout(() => onDone && onDone(), 900);
    } catch (err) {
      setStatus({ ok: false, msg: err.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-card wide">
      <h2>Submit a ticket</h2>
      <p className="muted">Tell us what's going on and we'll help.</p>
      <form onSubmit={submit}>
        <label>
          Subject
          <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} placeholder="Short summary" />
        </label>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
          <label style={{ flex: "1 1 200px", margin: 0 }}>
            Category
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              {TICKET_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label style={{ flex: "1 1 200px", margin: 0 }}>
            Area
            <select value={module} onChange={(e) => setModule(e.target.value)}>
              <option value="">— Select area —</option>
              {areaGroups.map((g) => (
                <optgroup key={g.label} label={g.label}>
                  {g.items.map((m) => <option key={m} value={m}>{m}</option>)}
                </optgroup>
              ))}
            </select>
          </label>
        </div>
        <label>
          Description
          <textarea value={body} onChange={(e) => setBody(e.target.value)} maxLength={5000} rows={6} placeholder="What happened? What did you expect?" />
        </label>
        <FilePicker files={files} setFiles={setFiles} disabled={busy} />
        <button className="btn" type="submit" disabled={busy}>{busy ? "Submitting…" : "Submit ticket"}</button>
      </form>
      {status && <p className={status.ok ? "success" : "error"}>{status.msg}</p>}
    </section>
  );
}
